/**
 * Signals Hook
 *
 * Manages standalone signals using React Query
 * Note: For agent-specific actions, use useAgentActions instead
 */

import type { AgentActionResponse } from '@monorepo/shared/schemas/agents'
import {
  ACTION_TYPE,
  AGGREGATION_TYPE,
  DEFAULT_UNIT_LABEL,
} from '@monorepo/shared/schemas/common'
import type {
  CreateSignal,
  PaginatedSignalsResponse,
  SignalQuery,
  UpdateSignal,
} from '@monorepo/shared/schemas/signals'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { signalService } from '../services/signalService'
import type { AgentActionFormData } from '../types/agent.types'
import { agentActionKeys } from './useAgentActions'
import { agentKeys } from './useAgents'

// Query keys
export const signalKeys = {
  all: ['signals'] as const,
  lists: () => [...signalKeys.all, 'list'] as const,
  list: (filters?: SignalQuery) => [...signalKeys.lists(), filters] as const,
  details: () => [...signalKeys.all, 'detail'] as const,
  detail: (id: string) => [...signalKeys.details(), id] as const,
}

/**
 * Fetch all signals
 */
export function useSignals(query?: SignalQuery) {
  return useQuery({
    queryKey: signalKeys.list(query),
    queryFn: () => signalService.getSignals(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single signal
 */
export function useSignal(id: string) {
  return useQuery({
    queryKey: signalKeys.detail(id),
    queryFn: () => signalService.getSignal(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Create new signal
 */
export function useCreateSignal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateSignal) => signalService.createSignal(data),
    onSuccess: newSignal => {
      // Invalidate signals list
      queryClient.invalidateQueries({
        queryKey: signalKeys.lists(),
      })

      // Add to cache
      queryClient.setQueryData(signalKeys.detail(newSignal.id), newSignal)

      toast.success(
        `Signal "${newSignal.signal_name}" has been created successfully.`
      )
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Update existing signal
 */
export function useUpdateSignal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSignal }) =>
      signalService.updateSignal(id, data),
    onSuccess: updatedSignal => {
      // Update specific signal in cache
      queryClient.setQueryData(
        signalKeys.detail(updatedSignal.id),
        updatedSignal
      )

      // Update signals list cache
      const matchingLists = queryClient.getQueriesData({
        queryKey: signalKeys.lists(),
      })
      for (const [key] of matchingLists) {
        queryClient.setQueryData(key, (existing: unknown) => {
          if (
            existing &&
            typeof existing === 'object' &&
            'data' in existing &&
            Array.isArray((existing as { data: unknown }).data)
          ) {
            const paginated = existing as PaginatedSignalsResponse
            return {
              ...paginated,
              data: paginated.data.map(s =>
                s.id === updatedSignal.id ? updatedSignal : s
              ),
            }
          }
          return existing
        })
      }

      // Invalidate lists to refresh
      queryClient.invalidateQueries({
        queryKey: signalKeys.lists(),
      })

      toast.success(
        `Signal "${updatedSignal.signal_name}" has been updated successfully.`
      )
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Delete signal
 */
export function useDeleteSignal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => signalService.deleteSignal(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: signalKeys.detail(id),
      })

      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: signalKeys.lists(),
      })

      toast.success('Signal has been deleted successfully.')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error))
    },
  })
}

/**
 * Agent Signals Hook - Fetches real agent actions data
 *
 * This hook provides agent actions (which the UI calls "signals")
 * with the structure expected by AgentDetail.
 */
export function useAgentSignals(agentId?: string) {
  const queryClient = useQueryClient()

  // Fetch agent actions (which are displayed as "signals" in the UI)
  const {
    data: actionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: agentActionKeys.list(agentId ?? '', { limit: 100 }),
    queryFn: async () => {
      if (!agentId) return null
      const { agentActionsService } = await import(
        '../services/agentActionsService'
      )
      return agentActionsService.getActions(agentId, { limit: 100 })
    },
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
  })

  const actions = actionsData?.data || []

  // Group actions by type (task, workflow, outcome)
  const actionsByType = {
    task: actions
      .filter(a => a.action_type === ACTION_TYPE.task)
      .map(transformActionToFormData),
    workflow: actions
      .filter(a => a.action_type === ACTION_TYPE.workflow)
      .map(transformActionToFormData),
    outcome: actions
      .filter(a => a.action_type === ACTION_TYPE.outcome)
      .map(transformActionToFormData),
  }

  // Calculate counts
  const counts = {
    billableSignals: actions.filter(a => a.billable_signal).length,
    costSignals: actions.reduce(
      (sum, a) => sum + (a.cost_signals?.length || 0),
      0
    ),
    total: actions.length,
  }

  // Placeholder functions for compatibility (not currently used)
  const createAction = useCallback(async (action: AgentActionFormData) => {
    toast.info('Use the form to create actions')
    return action
  }, [])

  const updateAction = useCallback(async (action: AgentActionFormData) => {
    toast.info('Action update not yet implemented')
    return action
  }, [])

  const deleteAction = useCallback(
    async (actionId: string) => {
      if (!agentId) return

      const { agentActionsService } = await import(
        '../services/agentActionsService'
      )
      await agentActionsService.deleteAction(agentId, actionId)

      // Invalidate all agent actions lists to refresh
      queryClient.invalidateQueries({
        queryKey: agentActionKeys.lists(),
      })

      // Invalidate agent detail to refresh actions count
      queryClient.invalidateQueries({
        queryKey: agentKeys.detail(agentId),
      })

      // Invalidate the agents list to refresh action counts in agent cards
      queryClient.invalidateQueries({
        queryKey: agentKeys.lists(),
      })

      toast.success('Action deleted successfully')
    },
    [agentId, queryClient]
  )

  const duplicateAction = useCallback(
    async (action: AgentActionFormData) => {
      if (!agentId) {
        toast.error('Agent ID is required')
        return action
      }

      try {
        const { agentActionsService } = await import(
          '../services/agentActionsService'
        )

        // Create new action based on existing action
        const duplicateData = {
          action_name: `${action.action_name} (Copy)`,
          action_type: action.action_type,
          description: action.description,

          // Billable signal (create new copy)
          billable_signal: {
            signal_name: `${action.signal_name} (Copy)`,
            signal_code: `${action.signal_code}_copy`,
            aggregation_type: action.aggregation_type,
            unit_label: action.unit_label ?? DEFAULT_UNIT_LABEL,
            value_field: action.value_field,
            description: `Copy of ${action.signal_name}`,
          },

          // Cost signals (link same cost signals)
          cost_signal_ids: action.cost_signal_ids ?? [],

          // Value metrics
          value: {
            fte_equivalent: action.fte_equivalent,
            revenue_per_unit: action.revenue_per_unit,
            default_price_cents: action.default_price_cents,
          },
        }

        await agentActionsService.createAction(agentId, duplicateData)

        // Invalidate all agent actions lists to refresh
        queryClient.invalidateQueries({
          queryKey: agentActionKeys.lists(),
        })

        // Invalidate agent detail to refresh actions count
        queryClient.invalidateQueries({
          queryKey: agentKeys.detail(agentId),
        })

        // Invalidate the agents list to refresh action counts in agent cards
        queryClient.invalidateQueries({
          queryKey: agentKeys.lists(),
        })

        toast.success(`Action "${action.action_name}" duplicated successfully`)
        return action
      } catch (error) {
        toast.error('Failed to duplicate action')
        throw error
      }
    },
    [agentId, queryClient]
  )

  return {
    actions: actionsByType,
    rawActions: actions, // Expose raw API responses for components that need full data
    counts,
    loading: isLoading,
    error: error ? String(error) : null,
    createAction,
    updateAction,
    deleteAction,
    duplicateAction,
  }
}

/**
 * Transform AgentActionResponse to AgentActionFormData for UI compatibility
 * Flattens the nested API structure into a flat form structure
 */
function transformActionToFormData(
  action: AgentActionResponse
): AgentActionFormData {
  const billableSignal = action.billable_signal

  return {
    id: action.id,
    // Action fields
    action_name: action.action_name,
    action_type: action.action_type,
    description: action.description,
    // Billable signal fields
    signal_name: billableSignal?.signal_name ?? action.action_name,
    signal_code: billableSignal?.signal_code ?? '',
    aggregation_type:
      billableSignal?.aggregation_type ?? AGGREGATION_TYPE.count,
    unit_label: billableSignal?.unit_label ?? DEFAULT_UNIT_LABEL,
    value_field: billableSignal?.value_field ?? undefined,
    // Cost signals
    cost_signal_ids: action.cost_signals?.map(cs => cs.signal_id) ?? [],
    // Revenue metrics (convert null to undefined for TypeScript)
    fte_equivalent: action.value?.fte_equivalent ?? undefined,
    revenue_per_unit: action.value?.revenue_per_unit ?? undefined,
    default_price_cents: action.value?.default_price_cents ?? undefined,
  }
}
